import React from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { Link as RouterLink } from 'react-router-dom'
import { safeGetJSON, safeSetJSON } from '../../utils/storage'
import { resolveClockItemTypes } from '../../utils/showClock'
import {
  attachClockDayItemPrep,
  listClockDayItems,
  listClockDays,
  lookupClockDay,
  type ClockDay,
  type ClockDayItem,
} from '../../api/showClockDays'
import type { ClockLiveProjectionItem } from '../../api/showClockLive'
import { addPrepItemToMyPrep } from '../../api/prep'
import useShowClockPrepUsageStore from '../../store/showClockPrepUsageStore'
import usePrepListStore from '../../store/prepListStore'

type PersistedStateV1 = {
  dayId?: number | null
  dayItemId?: number | null
  enabledTypeKeys?: string[] | null
}

const storageKeyForStation = (stationId: number) => `rpa:prep:addToClockState:v1:${stationId}`

const formatDayLabel = (day: ClockDay): string => {
  const dateLabel = day.date ? dayjs(day.date).format('dddd D MMMM') : 'Unknown date'
  const startTime = (() => {
    const start = typeof day.window_start === 'string' ? day.window_start : ''
    if (start.length >= 16) {
      return start.slice(11, 16)
    }
    return null
  })()
  const title = typeof day.title === 'string' ? day.title.trim() : ''

  const parts = [dateLabel]
  if (startTime) {
    parts.push(startTime)
  }
  if (title) {
    parts.push(title)
  }

  return parts.join(' • ')
}

const buildItemTypeMap = (): Map<string, string> => {
  const map = new Map<string, string>()
  resolveClockItemTypes().forEach((entry) => map.set(entry.key, entry.label))
  return map
}

const itemTypeMap = buildItemTypeMap()

const getTypeLabel = (key: string): string => itemTypeMap.get(key) ?? key

const extractTimeLabel = (value?: string | null): string | null => {
  if (!value) {
    return null
  }
  const trimmed = value.trim()
  if (trimmed.length >= 16) {
    return trimmed.slice(11, 16)
  }
  const match = trimmed.match(/(\d{2}):(\d{2})(?::\d{2})?$/)
  if (match) {
    return `${match[1]}:${match[2]}`
  }
  return null
}

const formatDayItemLabel = (item: ClockDayItem, scheduledTime?: string | null): string => {
  const typeLabel = getTypeLabel(item.item_type)
  const title = typeof item.title === 'string' ? item.title.trim() : ''
  const base = title ? `${typeLabel} — ${title}` : typeLabel
  return scheduledTime ? `[${scheduledTime}] ${base}` : base
}

const selectFirstMatchingItemId = (items: readonly ClockDayItem[], enabledTypes: ReadonlySet<string>): number | null => {
  for (const item of items) {
    const typeKey = item.item_type
    if (enabledTypes.has(typeKey)) {
      return item.id
    }
  }
  return items[0]?.id ?? null
}

export interface AddPrepToShowClockModalProps {
  open: boolean
  stationId: number
  prepItemId: number
  prepHeadline: string
  launchedFromMyPrep?: boolean
  onClose: () => void
  onSuccessToast?: (message: string) => void
  onAttached?: (params: { prepItemId: number; dayId: number; dayItemId: number }) => void
}

const AddPrepToShowClockModal: React.FC<AddPrepToShowClockModalProps> = ({
  open,
  stationId,
  prepItemId,
  prepHeadline,
  launchedFromMyPrep = false,
  onClose,
  onSuccessToast,
  onAttached,
}) => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)

  const [days, setDays] = React.useState<ClockDay[]>([])
  const [liveDayId, setLiveDayId] = React.useState<number | null>(null)
  const [nowStation, setNowStation] = React.useState<string | null>(null)

  const [selectedDayId, setSelectedDayId] = React.useState<number | null>(null)
  const [dayItems, setDayItems] = React.useState<ClockDayItem[]>([])
  const [dayProjectionItems, setDayProjectionItems] = React.useState<ClockLiveProjectionItem[]>([])
  const [selectedDayItemId, setSelectedDayItemId] = React.useState<number | null>(null)

  const allTypeKeys = React.useMemo(() => resolveClockItemTypes().map((entry) => entry.key), [])
  const [enabledTypeKeys, setEnabledTypeKeys] = React.useState<string[]>(allTypeKeys)
  const enabledTypeSet = React.useMemo(() => new Set(enabledTypeKeys), [enabledTypeKeys])

  const [addToMyPrep, setAddToMyPrep] = React.useState(true)
  const addToMyPrepDisabled = launchedFromMyPrep

  const optimisticAddUsage = useShowClockPrepUsageStore((state) => state.optimisticAdd)
  const optimisticAddMyId = usePrepListStore((state) => state.optimisticAddMyId)
  const bumpMyMembershipVersion = usePrepListStore((state) => state.bumpMyMembershipVersion)

  const selectedDay = React.useMemo(
    () => (selectedDayId ? days.find((day) => day.id === selectedDayId) ?? null : null),
    [days, selectedDayId],
  )
  const isLiveDay = selectedDayId !== null && liveDayId !== null && selectedDayId === liveDayId

  const projectionByItemId = React.useMemo(() => {
    const map = new Map<number, ClockLiveProjectionItem>()
    dayProjectionItems.forEach((entry) => {
      if (entry && typeof entry.id === 'number') {
        map.set(entry.id, entry)
      }
    })
    return map
  }, [dayProjectionItems])

  const persistState = React.useCallback(
    (partial: PersistedStateV1) => {
      if (!stationId || stationId <= 0) {
        return
      }

      const key = storageKeyForStation(stationId)
      const existing = safeGetJSON<PersistedStateV1>(key) ?? {}
      const next: PersistedStateV1 = {
        ...existing,
        ...partial,
      }
      safeSetJSON(key, next)
    },
    [stationId],
  )

  const eligibleDays = React.useMemo(() => {
    if (!nowStation) {
      return days
    }

    return days.filter((day) => {
      const end = typeof day.window_end === 'string' ? day.window_end.trim() : ''
      if (!end) {
        return true
      }
      return end > nowStation
    })
  }, [days, nowStation])

  const noEligibleDays = !loading && !error && eligibleDays.length === 0
  const showForm = !loading && !noEligibleDays

  React.useEffect(() => {
    if (!open) {
      return
    }

    setError(null)
    setSaving(false)
    setLoading(true)

    setAddToMyPrep(true)

    const persisted = stationId > 0 ? safeGetJSON<PersistedStateV1>(storageKeyForStation(stationId)) : null
    const persistedDayId = persisted?.dayId && Number.isFinite(persisted.dayId) ? Math.floor(Number(persisted.dayId)) : null
    const persistedDayItemId =
      persisted?.dayItemId && Number.isFinite(persisted.dayItemId) ? Math.floor(Number(persisted.dayItemId)) : null

    const persistedTypeKeys = Array.isArray(persisted?.enabledTypeKeys)
      ? persisted?.enabledTypeKeys.filter((key): key is string => typeof key === 'string' && key.trim().length > 0)
      : null

    const mergedEnabledTypes = (() => {
      if (!persistedTypeKeys || persistedTypeKeys.length === 0) {
        return allTypeKeys
      }
      const known = new Set(allTypeKeys)
      const normalized = persistedTypeKeys.map((key) => key.trim())
      const filtered = normalized.filter((key) => known.has(key))
      return filtered.length > 0 ? filtered : allTypeKeys
    })()

    setEnabledTypeKeys(mergedEnabledTypes)
    setSelectedDayItemId(persistedDayItemId)

    let cancelled = false
    const controller = new AbortController()

    const load = async () => {
      try {
        const lookup = await lookupClockDay({ stationId }, { signal: controller.signal })
        if (cancelled || controller.signal.aborted) {
          return
        }
        const liveId = lookup.current?.id ?? null
        setLiveDayId(liveId)
        setNowStation(typeof lookup.now === 'string' ? lookup.now : null)

        const list = await listClockDays({ stationId }, { signal: controller.signal })
        if (cancelled || controller.signal.aborted) {
          return
        }

        const nextDays = Array.isArray(list.days) ? list.days : []
        setDays(nextDays)

        const nowString = typeof lookup.now === 'string' ? lookup.now : null
        const eligible = nowString
          ? nextDays.filter((day) => {
              const end = typeof day.window_end === 'string' ? day.window_end.trim() : ''
              if (!end) {
                return true
              }
              return end > nowString
            })
          : nextDays

        const chosenDayId = (() => {
          if (persistedDayId && eligible.some((day) => day.id === persistedDayId)) {
            return persistedDayId
          }
          if (liveId && eligible.some((day) => day.id === liveId)) {
            return liveId
          }
          return eligible[0]?.id ?? null
        })()

        setSelectedDayId(chosenDayId)
        persistState({ dayId: chosenDayId })
      } catch (err) {
        if (cancelled || controller.signal.aborted) {
          return
        }
        setError(err instanceof Error ? err.message : 'Unable to load show clocks.')
      } finally {
        if (!cancelled && !controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [allTypeKeys, launchedFromMyPrep, open, persistState, stationId])

  React.useEffect(() => {
    if (!open) {
      return
    }
    if (!stationId || !selectedDayId) {
      setDayItems([])
      setDayProjectionItems([])
      return
    }

    let cancelled = false
    const controller = new AbortController()

    const loadItems = async () => {
      try {
        const response = await listClockDayItems(stationId, selectedDayId, { signal: controller.signal })
        if (cancelled || controller.signal.aborted) {
          return
        }
        const items = Array.isArray(response.items) ? response.items : []
        const projectionItems = Array.isArray(response.projection?.items) ? response.projection.items : []
        setDayItems(items)
        setDayProjectionItems(projectionItems)
        const resolveItemType = (id: number) => items.find((entry) => entry.id === id)?.item_type ?? ''
        const canKeep =
          selectedDayItemId &&
          items.some((entry) => entry.id === selectedDayItemId) &&
          enabledTypeSet.has(resolveItemType(selectedDayItemId))
        const nextSelection = canKeep ? selectedDayItemId : selectFirstMatchingItemId(items, enabledTypeSet)
        setSelectedDayItemId(nextSelection)
        persistState({ dayItemId: nextSelection })
      } catch (err) {
        if (cancelled || controller.signal.aborted) {
          return
        }
        setError(err instanceof Error ? err.message : 'Unable to load show clock items.')
      }
    }

    void loadItems()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [enabledTypeSet, open, persistState, selectedDayId, selectedDayItemId, stationId])

  const handleDayChange = (nextDayId: number | null) => {
    setSelectedDayId(nextDayId)
    persistState({ dayId: nextDayId })
  }

  const handleItemChange = (nextItemId: number | null) => {
    setSelectedDayItemId(nextItemId)
    persistState({ dayItemId: nextItemId })
  }

  const handleToggleType = (typeKey: string, checked: boolean) => {
    const nextSet = new Set(enabledTypeKeys)
    if (checked) {
      nextSet.add(typeKey)
    } else {
      nextSet.delete(typeKey)
    }
    const next = Array.from(nextSet)
    setEnabledTypeKeys(next)
    persistState({ enabledTypeKeys: next })
  }

  const allTypesSelected = enabledTypeKeys.length === allTypeKeys.length

  const handleToggleAllTypes = () => {
    const next = allTypesSelected ? [] : allTypeKeys
    setEnabledTypeKeys(next)
    persistState({ enabledTypeKeys: next })
  }

  const filteredItems = React.useMemo(() => {
    if (!enabledTypeKeys.length) {
      return dayItems
    }
    return dayItems.filter((item) => enabledTypeSet.has(item.item_type))
  }, [dayItems, enabledTypeKeys.length, enabledTypeSet])

  React.useEffect(() => {
    if (!open) {
      return
    }
    if (!selectedDayItemId) {
      const next = selectFirstMatchingItemId(filteredItems, enabledTypeSet)
      if (next) {
        setSelectedDayItemId(next)
        persistState({ dayItemId: next })
      }
      return
    }
    const existsInFiltered = filteredItems.some((item) => item.id === selectedDayItemId)
    if (!existsInFiltered) {
      const next = selectFirstMatchingItemId(filteredItems, enabledTypeSet)
      setSelectedDayItemId(next)
      persistState({ dayItemId: next })
    }
  }, [enabledTypeSet, filteredItems, open, persistState, selectedDayItemId])

  const selectedItem = React.useMemo(
    () => (selectedDayItemId ? dayItems.find((item) => item.id === selectedDayItemId) ?? null : null),
    [dayItems, selectedDayItemId],
  )
  const selectedProjectionItem = React.useMemo(
    () => (selectedDayItemId ? projectionByItemId.get(selectedDayItemId) ?? null : null),
    [projectionByItemId, selectedDayItemId],
  )
  const selectedScheduledTime = React.useMemo(() => {
    if (!selectedDayItemId) {
      return null
    }
    const projection = projectionByItemId.get(selectedDayItemId)
    const raw = projection?.scheduled_start ?? projection?.projected_start ?? null
    return extractTimeLabel(raw)
  }, [projectionByItemId, selectedDayItemId])

  const canSubmit = Boolean(stationId && prepItemId && selectedDayId && selectedDayItemId)

  const handleSubmit = async () => {
    if (!canSubmit || !selectedDayId || !selectedDayItemId) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      if (addToMyPrep && !addToMyPrepDisabled) {
        await addPrepItemToMyPrep(prepItemId)
        optimisticAddMyId(prepItemId)
        bumpMyMembershipVersion()
      }

      await attachClockDayItemPrep(stationId, selectedDayItemId, { prep_item_id: prepItemId })

      if (selectedDay) {
        optimisticAddUsage(stationId, prepItemId, {
          day_id: selectedDay.id,
          date: typeof selectedDay.date === 'string' ? selectedDay.date : null,
          window_start: typeof selectedDay.window_start === 'string' ? selectedDay.window_start : null,
          window_end: typeof selectedDay.window_end === 'string' ? selectedDay.window_end : null,
          title: typeof selectedDay.title === 'string' ? selectedDay.title : null,
          items:
            selectedItem && selectedDayItemId
              ? [
                  {
                    day_item_id: selectedDayItemId,
                    item_type: selectedItem.item_type,
                    title: selectedItem.title ?? null,
                    scheduled_start: selectedProjectionItem?.scheduled_start ?? null,
                    scheduled_end: selectedProjectionItem?.scheduled_end ?? null,
                  },
                ]
              : [],
        })
      }

      const dayLabel = selectedDay ? formatDayLabel(selectedDay) : 'Show Clock'
      const itemLabel = selectedItem ? formatDayItemLabel(selectedItem, selectedScheduledTime) : 'Show Clock item'
      const message = `${prepHeadline} added to ${itemLabel} in Show Clock ${dayLabel}`
      onSuccessToast?.(message)
      onAttached?.({ prepItemId, dayId: selectedDayId, dayItemId: selectedDayItemId })
      onClose()
    } catch (err) {
      const payload = (err as { data?: unknown })?.data
      const code = payload && typeof payload === 'object' && 'code' in payload ? String((payload as any).code) : null

      if (code === 'rpa_clock_day_item_prep_duplicate') {
        setError('That prep item is already attached to the selected show clock item.')
      } else {
        setError(err instanceof Error ? err.message : 'Unable to add prep item to show clock.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="add-prep-to-showclock-title">
      <DialogTitle id="add-prep-to-showclock-title">Add to Show Clock</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {prepHeadline}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a show clock and the item you want to attach this prep item to.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {noEligibleDays ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                There are no show clocks available to add this prep item to yet.
              </Typography>
              <Link component={RouterLink} to="/show-clock/days" sx={{ display: 'inline-block', mt: 1 }}>
                Create a show clock
              </Link>
            </Box>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {showForm ? (
            <>
              <FormControl fullWidth size="small" disabled={loading || saving || eligibleDays.length === 0}>
                <InputLabel id="add-prep-clock-label">Show Clock</InputLabel>
                <Select
                  labelId="add-prep-clock-label"
                  label="Show Clock"
                  value={selectedDayId ?? ''}
                  onChange={(event) => {
                    const raw = event.target.value
                    const parsed = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10)
                    handleDayChange(Number.isFinite(parsed) ? parsed : null)
                  }}
                >
                  {eligibleDays.map((day) => {
                    const label = formatDayLabel(day)
                    const isLive = liveDayId === day.id
                    return (
                      <MenuItem key={day.id} value={day.id}>
                        {label}
                        {isLive ? ' (LIVE)' : ''}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2">Filter item types</Typography>
                  <Button size="small" variant="text" onClick={handleToggleAllTypes} disabled={saving}>
                    {allTypesSelected ? 'Deselect all' : 'Select all'}
                  </Button>
                </Stack>
                <FormGroup
                  row
                  sx={{
                    '& .MuiFormControlLabel-label': { fontSize: '0.78rem' },
                  }}
                >
                  {resolveClockItemTypes().map((type) => (
                    <FormControlLabel
                      key={type.key}
                      control={
                        <Checkbox
                          size="small"
                          checked={enabledTypeSet.has(type.key)}
                          onChange={(_event, checked) => handleToggleType(type.key, checked)}
                          disabled={saving}
                        />
                      }
                      label={type.label}
                    />
                  ))}
                </FormGroup>
              </Box>

              <FormControl
                fullWidth
                size="small"
                disabled={loading || saving || !selectedDayId || filteredItems.length === 0}
              >
                <InputLabel id="add-prep-item-label">Clock item</InputLabel>
                <Select
                  labelId="add-prep-item-label"
                  label="Clock item"
                  value={selectedDayItemId ?? ''}
                  onChange={(event) => {
                    const raw = event.target.value
                    const parsed = typeof raw === 'number' ? raw : Number.parseInt(String(raw), 10)
                    handleItemChange(Number.isFinite(parsed) ? parsed : null)
                  }}
                >
                  {filteredItems.map((item) => {
                    const projection = projectionByItemId.get(item.id)
                    const scheduled = projection?.scheduled_start ?? projection?.projected_start ?? null
                    const scheduledEnd = projection?.scheduled_end ?? projection?.projected_end ?? null
                    const timeLabel = extractTimeLabel(scheduled)
                    const isPast =
                      Boolean(isLiveDay && nowStation && scheduledEnd) && String(scheduledEnd) < String(nowStation)

                    return (
                      <MenuItem key={item.id} value={item.id} sx={isPast ? { color: 'text.disabled' } : undefined}>
                        {formatDayItemLabel(item, timeLabel)}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={addToMyPrep}
                    onChange={(_event, checked) => setAddToMyPrep(checked)}
                    disabled={saving || addToMyPrepDisabled}
                  />
                }
                label="Add to My Prep Items"
              />
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {showForm ? (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || saving}>
            {saving ? 'Adding…' : 'Add'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}

export default AddPrepToShowClockModal
